import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { ToastContainer, toast, Bounce } from "react-toastify";
import { useHistory } from "react-router-dom";
import { Container } from '../components/styled.ts';
import { Typography } from '../components/typography/index.tsx';
import { useMobile } from '../hooks/isMobile.tsx';
import { API_URLS } from '../utils/apiConfig.ts';
import { Loading } from "../components/loading/index.tsx";
import {
  isLifeTimeFreePlan,
  lifeTimeFreeExcludedDescriptorIdsFromCatalog,
  PLAN_LIFE_TIME_FREE,
} from "../utils/planConstants.ts";
import {
  allDescriptorIds,
  changedCount,
  countEnabled,
  detectPreset,
  enabledIdsForPreset,
  enabledToMuted,
  matchesQuery,
  mutedIdsForSave,
  mutedToEnabled,
  orphanMutedIds,
  recommendedEnabledIds,
  sameIdSet,
  setGroupEnabled,
  sortedCategoryNames,
  toggleId,
  toggleState,
  visibleDescriptors,
  type PresetId,
  type ToggleState,
  type VisibilityFilter,
} from '../utils/notificationPreferences.ts';

interface Descriptor {
  id: number;
  category: string;
  descriptor: string;
  exclude: string;
  ltfExclude?: string | null;
}

interface DescriptorsByCategory {
  [category: string]: Descriptor[];
}

interface DescriptorsResponse {
  descriptorsByCategory: DescriptorsByCategory;
  totalRecords: number;
  totalCategories: number;
}

interface UserProfile {
  excludedNotificationDescriptors: number[] | null;
  subscribePlan?: string;
}

const VISIBILITY_OPTIONS: Array<{ id: VisibilityFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "muted", label: "Muted" },
  { id: "enabled", label: "Enabled" },
];

const NotificationFilters: React.FC = () => {
  const { isMobile } = useMobile();
  const history = useHistory();
  const token = localStorage.getItem("tokn");

  const [profilePlan, setProfilePlan] = useState<string | null>(null);
  const filtersLocked = isLifeTimeFreePlan(profilePlan);
  const [descriptors, setDescriptors] = useState<DescriptorsByCategory>({});
  /** UI works in enabled ids; the API stores muted ids and is converted on save. */
  const [enabledIds, setEnabledIds] = useState<number[]>([]);
  const [savedEnabledIds, setSavedEnabledIds] = useState<number[]>([]);
  /** Profile mutes for ids no longer in the catalog — must survive save. */
  const [preservedOrphanMutedIds, setPreservedOrphanMutedIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  /** True when the profile never loaded: saving would overwrite real preferences. */
  const [preferencesUnavailable, setPreferencesUnavailable] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [visibility, setVisibility] = useState<VisibilityFilter>("all");
  const [upgradeExpanded, setUpgradeExpanded] = useState(false);
  const unblockRef = useRef<(() => void) | null>(null);

  const allIds = useMemo(() => allDescriptorIds(descriptors), [descriptors]);
  const categories = useMemo(() => sortedCategoryNames(descriptors), [descriptors]);
  const totalAlertTypes = allIds.length;
  const mutedCount = Math.max(0, totalAlertTypes - enabledIds.length);
  const activePreset = useMemo(
    () => (totalAlertTypes === 0 ? "custom" : detectPreset(enabledIds, descriptors)),
    [enabledIds, descriptors, totalAlertTypes]
  );
  const isDirty = !sameIdSet(enabledIds, savedEnabledIds);
  const filtersEditable = !filtersLocked && totalAlertTypes > 0 && !preferencesUnavailable;
  const canSave = filtersEditable && isDirty && !saving;
  const query = searchTerm.trim().toLowerCase();
  const isSearching = query.length > 0;

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      // Do not clear preferencesUnavailable until the profile loads successfully.
      // Clearing it early on Retry would briefly unlock editing with empty ids.

      const descriptorsResponse = await fetch(API_URLS.DESCRIPTORS);
      if (!descriptorsResponse.ok) {
        const errorText = await descriptorsResponse.text();
        throw new Error(`Failed to load alert types: ${descriptorsResponse.status} ${errorText}`);
      }

      const descriptorsData: DescriptorsResponse = await descriptorsResponse.json();
      const catalog = descriptorsData.descriptorsByCategory;
      setDescriptors(catalog);
      const ids = allDescriptorIds(catalog);

      const profileResponse = await fetch(API_URLS.SUBSCRIBER, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!profileResponse.ok) {
        // Never fall back to catalog defaults here: a later save would replace the
        // subscriber's real mute list with defaults they never chose.
        setProfilePlan(null);
        setPreferencesUnavailable(true);
        setEnabledIds([]);
        setSavedEnabledIds([]);
        setPreservedOrphanMutedIds([]);
        toast.error("Couldn't load your notification settings. Retry before making changes.", {
          autoClose: 4000,
        });
        return;
      }

      const profileData: UserProfile = await profileResponse.json();
      const locked = isLifeTimeFreePlan(profileData.subscribePlan);
      setProfilePlan(profileData.subscribePlan ?? null);

      let mutedIds: number[];
      if (profileData.excludedNotificationDescriptors != null) {
        mutedIds = profileData.excludedNotificationDescriptors;
      } else if (locked) {
        mutedIds = lifeTimeFreeExcludedDescriptorIdsFromCatalog(catalog);
      } else {
        mutedIds = enabledToMuted(ids, recommendedEnabledIds(catalog));
      }

      const nextEnabled = mutedToEnabled(ids, mutedIds);
      setEnabledIds(nextEnabled);
      setSavedEnabledIds(nextEnabled);
      setPreservedOrphanMutedIds(orphanMutedIds(ids, mutedIds));
      setPreferencesUnavailable(false);

      if (locked) {
        const enabled = new Set(nextEnabled);
        const withEnabledRows = Object.entries(catalog)
          .filter(([, rows]) => rows.some((row) => enabled.has(row.id)))
          .map(([category]) => category);
        setExpandedCategories(new Set(withEnabledRows));
      }
    } catch (err) {
      setPreferencesUnavailable(true);
      toast.error(err instanceof Error ? err.message : "An error occurred", { autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      void loadData();
    } else {
      toast.error("No authentication token found. Please log in again.", { autoClose: 3000 });
      setLoading(false);
    }
  }, [token, loadData]);

  useEffect(() => {
    if (!isDirty || !filtersEditable) {
      return;
    }

    unblockRef.current = history.block("You have unsaved alert changes. Leave without saving?");
    const warnOnUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnOnUnload);

    return () => {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
      window.removeEventListener("beforeunload", warnOnUnload);
    };
  }, [isDirty, filtersEditable, history]);

  const saveMutedIds = async (mutedIds: number[]) => {
    const response = await fetch(API_URLS.SUBSCRIBER_NOTIFICATION_PREFERENCES, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(mutedIds),
    });
    if (response.ok) {
      return;
    }
    if (response.status === 403) {
      // Plan changed to Life Time Free since the page loaded — lock the UI to match.
      setProfilePlan(PLAN_LIFE_TIME_FREE);
      throw new Error("Free plan alerts are fixed. Upgrade to choose your own.");
    }
    throw new Error("Failed to save notification settings");
  };

  const handleSave = async () => {
    if (!canSave) return;
    try {
      setSaving(true);
      await saveMutedIds(mutedIdsForSave(allIds, enabledIds, preservedOrphanMutedIds));
      setSavedEnabledIds(enabledIds);
      toast.success("Alert settings saved", { autoClose: 2000 });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save settings", {
        autoClose: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => setEnabledIds(savedEnabledIds);

  const handlePreset = (preset: Exclude<PresetId, "custom">) => {
    if (!filtersEditable) return;
    setEnabledIds(enabledIdsForPreset(preset, descriptors));
  };

  const handleRowToggle = (id: number) => {
    if (!filtersEditable) return;
    setEnabledIds((prev) => toggleId(prev, id));
  };

  const handleCategoryToggle = (ids: number[], state: ToggleState) => {
    if (!filtersEditable) return;
    setEnabledIds((prev) => setGroupEnabled(prev, ids, state !== "enabled"));
  };

  const handleExpand = (category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const matchingCategories = useMemo(() => {
    return categories.filter((category) => {
      const rows = filtersLocked
        ? descriptors[category].filter((row) => enabledIds.includes(row.id))
        : descriptors[category];
      if (rows.length === 0) {
        return false;
      }
      if (!isSearching && (filtersLocked || visibility === "all")) {
        return true;
      }
      return visibleDescriptors(
        rows,
        enabledIds,
        isSearching ? query : "",
        filtersLocked ? "all" : visibility
      ).length > 0;
    });
  }, [
    categories,
    descriptors,
    enabledIds,
    filtersLocked,
    isSearching,
    query,
    visibility,
  ]);

  const matchCount = useMemo(() => {
    if (!isSearching) return 0;
    return matchingCategories.reduce(
      (total, category) =>
        total + descriptors[category].filter((row) => matchesQuery(row, query)).length,
      0
    );
  }, [descriptors, isSearching, matchingCategories, query]);

  if (loading) {
    return <Loading />;
  }

  const dirtyCount = changedCount(allIds, enabledIds, savedEnabledIds);
  // While searching, matches are revealed rather than toggled open by hand.
  const isExpanded = (category: string) =>
    isSearching ? true : expandedCategories.has(category);

  const emptyListMessage = (() => {
    if (isSearching) {
      return `No alert types match "${searchTerm}".`;
    }
    switch (visibility) {
      case "all":
        return "No alert types to show.";
      case "enabled":
        return "No alert types are enabled right now.";
      case "muted":
        return "No alert types are muted right now.";
      default: {
        const exhaustive: never = visibility;
        return exhaustive;
      }
    }
  })();

  const renderRows = (category: string) => {
    const rows = filtersLocked
      ? descriptors[category].filter((row) => enabledIds.includes(row.id))
      : descriptors[category];
    const shown = visibleDescriptors(rows, enabledIds, query, filtersLocked ? "all" : visibility);

    if (shown.length === 0) {
      return <EmptyRow>No alert types match the current view.</EmptyRow>;
    }

    return shown.map((row) => {
      const isEnabled = enabledIds.includes(row.id);
      return (
        <TypeRow key={row.id}>
          {filtersLocked ? (
            <TypeName>{row.descriptor}</TypeName>
          ) : (
            <TypeLabel>
              <TypeCheckbox
                type="checkbox"
                role="switch"
                checked={isEnabled}
                onChange={() => handleRowToggle(row.id)}
              />
              <TypeName>{row.descriptor}</TypeName>
            </TypeLabel>
          )}
          {!filtersLocked && (
            <RowMeta $indent>
              <StatusText $enabled={isEnabled}>{isEnabled ? "Enabled" : "Muted"}</StatusText>
            </RowMeta>
          )}
        </TypeRow>
      );
    });
  };

  return (
    <PageContainer column $reserveActionBar={filtersEditable && isDirty}>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Bounce}
        aria-label="toast"
      />

      <Container column style={{ marginBottom: "20px" }}>
        <Typography
          fontFamily="Neue Montreal"
          fontSize={isMobile ? "28px" : "50px"}
          fontWeight={500}
          lineHeight={isMobile ? "43.2px" : "60px"}
          letterSpacing="-0.01em"
          color="#202020"
          display="inline"
          textAlign="center"
        >
          Notification{" "}
          <Typography
            tag="span"
            background="linear-gradient(165.56deg, #5395FF 39.75%, #D69DFF 58.69%)"
            WebkitBackgroundClip="text"
            WebkitTextFillColor="transparent"
          >
            Filters
          </Typography>
        </Typography>
      </Container>

      {preferencesUnavailable && (
        <ErrorBanner column>
          <BannerTitle>Couldn't load your alert settings</BannerTitle>
          <BannerBody>
            Editing is turned off so we don't overwrite settings you already have. Retry to load
            them again.
          </BannerBody>
          <RetryButton type="button" onClick={() => void loadData()}>
            Retry
          </RetryButton>
        </ErrorBanner>
      )}

      {filtersLocked && (
        <PlanBanner column>
          <BannerTitle>These alerts are included on your Free plan</BannerTitle>
          <BannerBody>Upgrade to enable any alert type and build your own set.</BannerBody>
          <UpgradeButton type="button" onClick={() => history.push("/subscription-plans")}>
            Upgrade now
          </UpgradeButton>
        </PlanBanner>
      )}

      {filtersEditable && (
        <>
          <PresetStatus role="status" aria-live="polite">
            <PresetCopy>
              <PresetTitle>
                {activePreset === "recommended" ? "Using recommended alerts" : "Using a custom selection"}
              </PresetTitle>
              <PresetHint>
                {activePreset === "recommended"
                  ? "Mute or enable any alert type below to make it your own."
                  : "Reset to the recommended set if you want high-signal alerts and less noise."}
              </PresetHint>
            </PresetCopy>
            {activePreset === "custom" && (
              <GhostButton type="button" onClick={() => handlePreset("recommended")}>
                Use recommended
              </GhostButton>
            )}
          </PresetStatus>

          <ControlsRow>
            <SearchField>
              <BlockLabel htmlFor="alert-search">Search alert types</BlockLabel>
              <SearchInputRow>
                <SearchInput
                  id="alert-search"
                  type="search"
                  placeholder="Search by name or category…"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  aria-describedby={isSearching ? "alert-search-count" : undefined}
                />
                {searchTerm && (
                  <GhostButton type="button" onClick={() => setSearchTerm("")}>
                    Clear
                  </GhostButton>
                )}
              </SearchInputRow>
              {isSearching && (
                <HelpText id="alert-search-count" role="status">
                  {matchCount} matching alert type{matchCount === 1 ? "" : "s"}
                </HelpText>
              )}
            </SearchField>

            <VisibilityField>
              <BlockLabel as="span" id="visibility-label">
                Show
              </BlockLabel>
              <SegmentedGroup role="group" aria-labelledby="visibility-label">
                {VISIBILITY_OPTIONS.map((option) => (
                  <SegmentButton
                    key={option.id}
                    type="button"
                    aria-pressed={visibility === option.id}
                    $selected={visibility === option.id}
                    onClick={() => setVisibility(option.id)}
                  >
                    {option.label}
                  </SegmentButton>
                ))}
              </SegmentedGroup>
            </VisibilityField>
          </ControlsRow>
        </>
      )}

      {!preferencesUnavailable && (
        <CategoryList>
          {matchingCategories.map((category) => {
            const rows = descriptors[category];
            const ids = rows.map((row) => row.id);
            const enabledInCategory = countEnabled(ids, enabledIds);
            if (filtersLocked && enabledInCategory === 0) {
              return null;
            }

            const state = toggleState(ids, enabledIds);
            const categoryStatusLabel = (() => {
              switch (state) {
                case "enabled":
                  return "Enabled";
                case "muted":
                  return "Muted";
                case "partial":
                  return `${enabledInCategory} enabled`;
                default: {
                  const exhaustive: never = state;
                  return exhaustive;
                }
              }
            })();
            const expanded = isExpanded(category);
            const panelId = `alert-panel-${category.replace(/\W+/g, "-")}`;
            const headerId = `alert-header-${category.replace(/\W+/g, "-")}`;

            return (
              <CategoryCard key={category}>
                <CategoryHeader>
                  <CategoryToggleButton
                    type="button"
                    id={headerId}
                    aria-expanded={expanded}
                    aria-controls={panelId}
                    onClick={() => handleExpand(category)}
                  >
                    <CategoryTitleRow>
                      <CategoryName>{category}</CategoryName>
                      <Chevron $expanded={expanded} aria-hidden="true">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path
                            d="M4 6l4 4 4-4"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </Chevron>
                    </CategoryTitleRow>
                    <CountText>
                      {filtersLocked
                        ? `${enabledInCategory} enabled`
                        : `${enabledInCategory} of ${rows.length} enabled`}
                    </CountText>
                  </CategoryToggleButton>
                  {filtersEditable && (
                    <CategorySwitch
                      type="button"
                      $state={state}
                      onClick={() => handleCategoryToggle(ids, state)}
                      aria-label={`${state === "enabled" ? "Mute" : "Enable"} all alert types in ${category}`}
                    >
                      {categoryStatusLabel}
                    </CategorySwitch>
                  )}
                </CategoryHeader>
                <CategoryPanel id={panelId} role="region" aria-labelledby={headerId} hidden={!expanded}>
                  {renderRows(category)}
                </CategoryPanel>
              </CategoryCard>
            );
          })}
          {matchingCategories.length === 0 && <EmptyRow>{emptyListMessage}</EmptyRow>}
        </CategoryList>
      )}

      {filtersLocked && mutedCount > 0 && (
        <UpgradeBlock>
          <UpgradeToggle
            type="button"
            aria-expanded={upgradeExpanded}
            aria-controls="more-with-paid"
            onClick={() => setUpgradeExpanded((value) => !value)}
          >
            <span>{mutedCount} more alert types with a paid plan</span>
            <Chevron $expanded={upgradeExpanded} aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M4 6l4 4 4-4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Chevron>
          </UpgradeToggle>
          <UpgradePanel id="more-with-paid" hidden={!upgradeExpanded}>
            <BannerBody>
              Paid plans unlock every alert type in the catalog, and let you pick exactly which ones
              reach your WhatsApp.
            </BannerBody>
            <UpgradeButton type="button" onClick={() => history.push("/subscription-plans")}>
              Upgrade now
            </UpgradeButton>
          </UpgradePanel>
        </UpgradeBlock>
      )}

      {filtersEditable &&
        isDirty &&
        /* Portalled to the body so no ancestor's stacking or clipping context can
           push the only way to save off-screen. */
        createPortal(
          <ActionBar>
            <ActionBarInner>
              <DirtyText role="status" aria-live="polite">
                {dirtyCount} unsaved change{dirtyCount === 1 ? "" : "s"}
              </DirtyText>
              <ButtonRow>
                <DiscardButton type="button" onClick={handleDiscard} disabled={saving}>
                  Discard
                </DiscardButton>
                <SaveButton type="button" onClick={() => void handleSave()} disabled={!canSave}>
                  {saving ? "Saving…" : "Save changes"}
                </SaveButton>
              </ButtonRow>
            </ActionBarInner>
          </ActionBar>,
          document.body
        )}
    </PageContainer>
  );
};

export default NotificationFilters;

// Styled Components
const PageContainer = styled(Container)<{ $reserveActionBar?: boolean }>`
  width: min(calc(100% - 40px), 700px);
  margin: 10px auto;
  /* Bottom room so the fixed save bar never covers the last category. */
  padding: ${({ $reserveActionBar }) =>
    $reserveActionBar ? "20px 20px 120px 20px" : "20px"};
  justify-content: center;
  box-sizing: border-box;

  @media (max-width: 767px) {
    width: 100%;
    padding: ${({ $reserveActionBar }) =>
      $reserveActionBar ? "12px 0 132px 0" : "12px 0"};
  }
`;

const BannerTitle = styled.p`
  margin: 0;
  font-family: "Neue Montreal", sans-serif;
  font-size: 15px;
  font-weight: 500;
  line-height: 22px;
  color: inherit;
`;

const BannerBody = styled.p`
  margin: 0;
  font-family: "Neue Montreal", sans-serif;
  font-size: 13px;
  line-height: 20px;
  color: inherit;
`;

const ErrorBanner = styled(Container)`
  background: #f8d7da;
  border: 1px solid #f5c2c7;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  gap: 10px;
  color: #842029;
`;

const PlanBanner = styled(Container)`
  background: #fff3cd;
  border: 1px solid #ffecb5;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  gap: 10px;
  color: #664d03;
`;

const BaseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  border-radius: 100px;
  font: inherit;
  font-family: "Neue Montreal", sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 19.6px;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &:focus-visible {
    outline: 2px solid #5395ff;
    outline-offset: 2px;
  }
`;

const RetryButton = styled(BaseButton)`
  padding: 8px 20px;
  width: fit-content;
  color: #fff;
  background: #842029;
`;

const UpgradeButton = styled(BaseButton)`
  padding: 8px 20px;
  width: fit-content;
  color: #fff;
  background: linear-gradient(165.56deg, #5395ff 39.75%, #d69dff 58.69%);
`;

const PresetStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  margin: 0 0 20px 0;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  background: #f8f9fa;
  box-sizing: border-box;

  @media (max-width: 520px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const PresetCopy = styled.div`
  min-width: 0;
`;

const PresetTitle = styled.div`
  font-family: "Neue Montreal", sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 19.6px;
  letter-spacing: 0.02em;
  color: #202020;
`;

const PresetHint = styled.div`
  font-family: "Neue Montreal", sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #666;
  margin-top: 2px;
`;

const BlockLabel = styled.label`
  display: block;
  padding: 0;
  font-family: "Neue Montreal", sans-serif;
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  color: #555;
  margin-bottom: 8px;
`;

const ControlsRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  width: 100%;
  margin-bottom: 20px;

  @media (max-width: 767px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const SearchField = styled.div`
  flex: 1;
  min-width: 0;
  width: 100%;
`;

const VisibilityField = styled.div`
  flex-shrink: 0;
`;

const SearchInputRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 11px 14px;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  font-family: "Neue Montreal", sans-serif;
  font-size: 16px;
  background: #fff;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #5395ff;
    box-shadow: 0 0 0 3px rgba(83, 149, 255, 0.15);
  }

  &::placeholder {
    color: #6c757d;
  }
`;

const GhostButton = styled(BaseButton)`
  padding: 10px 14px;
  border: 1px solid #e9ecef;
  background: #fff;
  color: #555;
  white-space: nowrap;
`;

const HelpText = styled.p`
  margin: 6px 0 0;
  font-family: "Neue Montreal", sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #666;
`;

const SegmentedGroup = styled.div`
  display: inline-flex;
  border: 2px solid #e9ecef;
  border-radius: 100px;
  overflow: hidden;
  background: #fff;
`;

const SegmentButton = styled.button<{ $selected: boolean }>`
  cursor: pointer;
  border: none;
  padding: 10px 16px;
  font: inherit;
  font-family: "Neue Montreal", sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 19.6px;
  color: ${({ $selected }) => ($selected ? "#fff" : "#555")};
  background: ${({ $selected }) => ($selected ? "#5395ff" : "transparent")};

  &:focus-visible {
    outline: 2px solid #5395ff;
    outline-offset: -2px;
  }
`;

const CategoryList = styled.div`
  width: 100%;
`;

const CategoryCard = styled.div`
  background: #fff;
  border-radius: 12px;
  margin-bottom: 12px;
  border: 1px solid #e9ecef;
  overflow: hidden;
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 12px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
`;

/* The name gets its own full-width line; the count sits beneath it. Long
   regulatory category names are unreadable when squeezed beside the controls. */
const CategoryToggleButton = styled.button`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 3px;
  padding: 12px 14px;
  cursor: pointer;
  border: none;
  background: transparent;
  font: inherit;
  text-align: left;
  color: inherit;
  min-width: 0;

  &:hover {
    background: #e9ecef;
  }

  &:focus-visible {
    outline: 2px solid #5395ff;
    outline-offset: -2px;
  }
`;

const CategoryTitleRow = styled.span`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  color: #6c757d;
`;

const CategoryName = styled.span`
  flex: 1;
  min-width: 0;
  font-family: "Neue Montreal", sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 22.4px;
  letter-spacing: 0.01em;
  color: #202020;
  overflow-wrap: break-word;

  @media (max-width: 767px) {
    font-size: 15px;
    line-height: 21px;
  }
`;

const CountText = styled.span`
  font-family: "Neue Montreal", sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #6c757d;
  white-space: nowrap;
`;

const Chevron = styled.span<{ $expanded: boolean }>`
  display: inline-flex;
  flex-shrink: 0;
  margin-top: 3px;
  transform: ${({ $expanded }) => ($expanded ? "rotate(180deg)" : "rotate(0deg)")};
  transition: transform 0.2s ease;
`;

/* A compact pill, not a full-height colour block: the category name should stay
   the loudest thing in the header. */
const CategorySwitch = styled.button<{ $state: ToggleState }>`
  cursor: pointer;
  flex-shrink: 0;
  height: 34px;
  padding: 0 14px;
  border-radius: 999px;
  font: inherit;
  font-family: "Neue Montreal", sans-serif;
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;

  border: 1px solid
    ${({ $state }) =>
      $state === "enabled" ? "#059669" : $state === "partial" ? "#fbbf24" : "#fecaca"};
  color: ${({ $state }) =>
    $state === "enabled" ? "#fff" : $state === "partial" ? "#d97706" : "#dc2626"};
  background: ${({ $state }) =>
    $state === "enabled" ? "#059669" : $state === "partial" ? "#fffbeb" : "#fef2f2"};

  &:hover {
    opacity: 0.9;
  }

  &:focus-visible {
    outline: 2px solid #5395ff;
    outline-offset: -2px;
  }
`;

const CategoryPanel = styled.div`
  padding: 6px 16px 10px;
  background: #fff;

  &[hidden] {
    display: none;
  }
`;

/* On phones the badge and status move to their own line, so long regulatory
   names get the full width instead of wrapping into a narrow column. */
const TypeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 767px) {
    flex-wrap: wrap;
    gap: 6px;
  }
`;

const TypeLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
  cursor: pointer;

  @media (max-width: 767px) {
    flex: 1 1 100%;
  }
`;

const RowMeta = styled.span<{ $indent?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;

  @media (max-width: 767px) {
    width: 100%;
    /* Line up under the name, clearing the checkbox when there is one. */
    padding-left: ${({ $indent }) => ($indent ? "30px" : "0")};
  }
`;

const TypeCheckbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
  flex-shrink: 0;

  &:focus-visible {
    outline: 2px solid #5395ff;
    outline-offset: 2px;
  }
`;

const TypeName = styled.span`
  flex: 1;
  min-width: 0;
  font-family: "Neue Montreal", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  letter-spacing: 0.02em;
  color: #202020;
  word-break: break-word;
`;

/* Match admin-ui badge tones: Enabled = success green, Muted = failed red. */
const StatusText = styled.span<{ $enabled?: boolean }>`
  font-family: "Neue Montreal", sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  padding: 2px 8px;
  border-radius: 999px;
  white-space: nowrap;
  flex-shrink: 0;
  color: ${({ $enabled }) => ($enabled ? "#059669" : "#dc2626")};
  background: ${({ $enabled }) => ($enabled ? "#ecfdf5" : "#fef2f2")};
`;

const EmptyRow = styled.p`
  margin: 0;
  padding: 16px 0;
  text-align: center;
  font-family: "Neue Montreal", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  letter-spacing: 0.02em;
  color: #666;
`;

const UpgradeBlock = styled.div`
  width: 100%;
  margin-top: 8px;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  overflow: hidden;
  background: #f8f9fa;
`;

const UpgradeToggle = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  cursor: pointer;
  border: none;
  background: transparent;
  font: inherit;
  font-family: "Neue Montreal", sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 19.6px;
  color: #202020;
  text-align: left;

  &:hover {
    background: #e9ecef;
  }

  &:focus-visible {
    outline: 2px solid #5395ff;
    outline-offset: -2px;
  }
`;

const UpgradePanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 16px 16px;
  color: #555;

  &[hidden] {
    display: none;
  }
`;

/* Fixed rather than sticky: as the last child of a long page, a sticky bar only
   pins within the few pixels of container left below it, so it stayed out of sight
   until the user scrolled past every category. */
const ActionBar = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  background: #fff;
  border-top: 1px solid #e9ecef;
  box-shadow: 0 -6px 18px rgba(0, 0, 0, 0.1);
  padding: 12px 20px calc(12px + env(safe-area-inset-bottom, 0px));
  box-sizing: border-box;

  @media (max-width: 767px) {
    padding: 10px 15px calc(10px + env(safe-area-inset-bottom, 0px));
  }
`;

const ActionBarInner = styled.div`
  width: min(100%, 700px);
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  @media (max-width: 520px) {
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
  }
`;

const DirtyText = styled.p`
  margin: 0;
  min-width: 0;
  font-family: "Neue Montreal", sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 21px;
  letter-spacing: 0.02em;
  color: #202020;

  @media (max-width: 520px) {
    text-align: center;
    width: 100%;
    font-size: 13px;
    line-height: 20px;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;

  @media (max-width: 520px) {
    width: 100%;

    > * {
      flex: 1;
    }
  }
`;

const DiscardButton = styled(BaseButton)`
  padding: 11px 20px;
  color: #fff;
  background: #6c757d;
`;

const SaveButton = styled(BaseButton)`
  padding: 11px 22px;
  color: #fff;
  background: linear-gradient(165.56deg, #5395ff 39.75%, #d69dff 58.69%);
`;
